import React, { useState, useCallback } from "react";
import { Box, Button, Typography, Link } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { PostcardCollage } from "../components/landing/PostcardCollage";

const MotionBox = motion(Box);

const fadeUpBlur = (delay: number) => ({
  initial: { opacity: 0, y: 20, filter: "blur(8px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
  transition: {
    duration: 0.6,
    delay,
    ease: [0.25, 0.46, 0.45, 0.94],
  },
});

const fadeUpBlurReduced = {
  initial: { opacity: 1, y: 0, filter: "blur(0px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
  transition: { duration: 0 },
};

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();
  const [collageComplete, setCollageComplete] =
    useState(!!prefersReducedMotion);

  const handleCollageComplete = useCallback(() => {
    setCollageComplete(true);
  }, []);

  // const getAnimation = (delay: number) =>
  //   prefersReducedMotion || collageComplete === false
  //     ? prefersReducedMotion
  //       ? fadeUpBlurReduced
  //       : {} // empty until collage completes
  //     : fadeUpBlur(delay);

  const showText = prefersReducedMotion || collageComplete;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "#FDF8F5",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Postcard collage background */}
      <PostcardCollage onComplete={handleCollageComplete} />

      {/* Centered content card */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: { xs: "calc(100% - 40px)", sm: 420, md: 480 },
          width: "100%",
          mx: "auto",
          px: { xs: 3, sm: 4 },
          py: { xs: 4, sm: 5 },
          borderRadius: "20px",
          background: "rgba(255, 255, 255, 0.65)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          boxShadow:
            "0 8px 32px rgba(21, 37, 63, 0.08), 0 2px 8px rgba(21, 37, 63, 0.04)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Logo */}
        {showText && (
          <MotionBox
            {...(prefersReducedMotion ? fadeUpBlurReduced : fadeUpBlur(0))}
          >
            <Typography
              level="h1"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "2.5rem", sm: "3rem" },
                fontWeight: 700,
                mb: 2,
                color: "#15253F",
                letterSpacing: "-0.01em",
              }}
            >
              Listas
            </Typography>
          </MotionBox>
        )}

        {/* Decorative accent line */}
        {showText && (
          <MotionBox
            {...(prefersReducedMotion ? fadeUpBlurReduced : fadeUpBlur(0.15))}
            sx={{
              width: 48,
              height: 3,
              borderRadius: 2,
              background: "linear-gradient(90deg, #C8912A, #FF6B4A)",
              mb: 3,
            }}
          />
        )}

        {/* Tagline */}
        {showText && (
          <MotionBox
            {...(prefersReducedMotion ? fadeUpBlurReduced : fadeUpBlur(0.2))}
          >
            <Typography
              level="h2"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "1.5rem", sm: "1.75rem" },
                fontWeight: 600,
                mb: 0.5,
                color: "#15253F",
                lineHeight: 1.3,
              }}
            >
              Travel with intention.
            </Typography>
          </MotionBox>
        )}
        {showText && (
          <MotionBox
            {...(prefersReducedMotion ? fadeUpBlurReduced : fadeUpBlur(0.4))}
          >
            <Typography
              level="h2"
              sx={{
                fontFamily: "'Playfair Display', serif",
                fontSize: { xs: "1.5rem", sm: "1.75rem" },
                fontWeight: 600,
                mb: 4,
                color: "#6B6B6B",
                lineHeight: 1.3,
              }}
            >
              Remember every place.
            </Typography>
          </MotionBox>
        )}

        {/* CTA buttons */}
        {showText && (
          <MotionBox
            {...(prefersReducedMotion ? fadeUpBlurReduced : fadeUpBlur(0.6))}
            sx={{ width: "100%" }}
          >
            <Button
              fullWidth
              size="lg"
              onClick={() => navigate("/auth?mode=signup")}
              sx={{
                backgroundColor: "#FF6B4A",
                "&:hover": { backgroundColor: "#E55A3A" },
                mb: 2,
                fontWeight: 600,
                fontSize: "1rem",
                py: 1.5,
                borderRadius: "12px",
              }}
            >
              Sign up with email
            </Button>

            <Typography level="body-sm" sx={{ color: "#6B6B6B" }}>
              Already have an account?{" "}
              <Link
                component="button"
                onClick={() => navigate("/auth?mode=signin")}
                sx={{ cursor: "pointer" }}
              >
                Sign in
              </Link>
            </Typography>
          </MotionBox>
        )}
      </Box>

      {/* Terms footer */}
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: "center",
          zIndex: 1,
        }}
      >
        <Link
          href="/terms-and-conditions.pdf"
          target="_blank"
          rel="noopener noreferrer"
          level="body-xs"
          sx={{ color: "#6B6B6B" }}
        >
          Terms and Conditions
        </Link>
      </Box>
    </Box>
  );
};
